/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import * as React from 'react';

import {
    BLOCK_CODE,
    BLOCK_CODE_LINE,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_IMAGE,
    BLOCK_IMAGES,
    BLOCK_LIST_ITEM,
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    BLOCK_PARAGRAPH,
    INLINE_EMOJI,
    INLINE_IMAGE,
    INLINE_LINK,
} from '@gitbook/doc-core';
import { parseMarkdown } from '@gitbook/doc-format';
import { type ChildNode, type Document, Value } from '@gitbook/slate';
import { Editor, type EditorContainer } from '@gitbook/slate-react';
import { useLiveRef } from '@gitbook/util-react-hooks';
import { createCache } from '@gitbook/util-suspense-cache';

import { useSlateCodeHighlighter } from '../CodeBlocks';
import { type PlainMarkdownKind, PlainMarkdownProvider } from './PlainMarkdownProvider';
import { renderMark, renderNode } from './renderer';

const markdownCache = createCache<string, Awaited<ReturnType<typeof parseMarkdown>>>({
    getKey: (markdown) => markdown,
    getValue: (markdown: string) => {
        return parseMarkdown(markdown);
    },
});

const plugins = [{ renderNode, renderMark }];

export type PlainMarkdownProps = {
    input: string | Document;
    /** Controls the styling of the markdown nodes when rendered */
    kind?: PlainMarkdownKind;
    /** Pass down style to the editor */
    style?: React.CSSProperties;
    /** Allow block */
    allowBlock?: (node: ChildNode) => boolean;
};

/**
 * We want to have a lean document after parsing the markdown
 * so remove the nodes that are not supported by the PlainMarkdown
 * component.
 */
function defaultAllowBlock(node: ChildNode): boolean {
    switch (node.object) {
        case 'block':
            return [
                BLOCK_PARAGRAPH,
                BLOCK_HEADING_1,
                BLOCK_HEADING_2,
                BLOCK_HEADING_3,
                BLOCK_LIST_UL,
                BLOCK_LIST_OL,
                BLOCK_LIST_TASKS,
                BLOCK_LIST_ITEM,
                BLOCK_IMAGES,
                BLOCK_IMAGE,
                BLOCK_CODE,
                BLOCK_CODE_LINE,
            ].includes(node.type);

        case 'inline':
            return [INLINE_LINK, INLINE_EMOJI, INLINE_IMAGE].includes(node.type);

        case 'text':
            return true;

        default:
            return false;
    }
}

/**
 * The PlainMarkdown component renders basic markdown syntax and leverages
 * a very lean version of the Slate editor (readonly) by filtering out nodes
 * and marks that are not supported by this.
 */
export function PlainMarkdown(props: PlainMarkdownProps): React.ReactElement {
    const editorRef = React.useRef<EditorContainer>(null);
    const { input, kind = 'default', allowBlock = defaultAllowBlock } = props;
    const allowBlockRef = useLiveRef(allowBlock);

    const value = React.useMemo(() => {
        const document = typeof input === 'string' ? markdownCache.read(input).document : input;
        return Value.create({
            document: document.mapDescendants((node) =>
                allowBlockRef.current(node) ? node : null
            ),
        });
    }, [input, allowBlockRef]);
    const codeHighlighterPlugin = useSlateCodeHighlighter(value.document, BLOCK_CODE);

    return (
        <PlainMarkdownProvider kind={kind}>
            <Editor
                ref={editorRef}
                value={value}
                onChange={() => {}}
                virtualize={false}
                enableBlockContainment
                readOnly
                plugins={[...plugins, codeHighlighterPlugin]}
                style={props.style}
            />
        </PlainMarkdownProvider>
    );
}
